import { FC, useCallback, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useCsrfToken,
  useCurrentUser,
  useLogin,
  isMfaRequiredResponse,
  ApiError,
} from '@centrix/api-client';
import type { AuthUser, LoginResponse } from '@centrix/api-client';
import {
  Alert,
  Button,
  CollapsibleContainer,
  FormInputField,
  Heading,
  Paragraph,
  PasswordInputField,
  Spinner,
} from '@centrix/shared-ui';
import { getMfeAppUrl, isAdminUser } from '@centrix/types';

import { AuthLayout } from '@/components/AuthLayout';
import { MfaChallengeForm } from '@/components/Login/MfaChallengeForm';
import { type CredentialsValidationErrors, validateCredentials } from '@/utils/authValidation';

// Default redirect after login - goes to LMS learner dashboard
const LMS_BASE_URL = import.meta.env.VITE_LMS_BASE_URL || 'http://localhost:18000';
const LEARNER_REDIRECT = getMfeAppUrl('learning', {
  env: import.meta.env,
  isDev: import.meta.env.DEV,
  // Dev port so the redirect lands on the learning MFE dev server
  // (e.g. http://apps.local.openedx.io:3005/learning/). Ignored in prod
  // where path-based routing is used (isDev is false).
  port: 3005,
});
const ADMIN_REDIRECT = getMfeAppUrl('admin', {
  env: import.meta.env,
  isDev: import.meta.env.DEV,
  // Dev port so the redirect lands on the admin MFE dev server
  // (http://apps.local.openedx.io:3001/admin/) instead of the portless
  // host, which 404s. Ignored in prod (path-based routing, isDev false).
  port: 3001,
});

const STUDIO_BASE_URL = import.meta.env.VITE_STUDIO_BASE_URL as string | undefined;

// All MFE apps are served from the same origin in production.
// LMS and Studio origins are allowed to support OAuth2 SSO redirects.
const ALLOWED_REDIRECT_ORIGINS = new Set(
  [
    LMS_BASE_URL,
    STUDIO_BASE_URL,
    typeof window !== 'undefined' ? window.location.origin : undefined,
  ]
    .filter((value): value is string => Boolean(value))
    .map((value) => {
      try {
        return new URL(value).origin;
      } catch {
        return value;
      }
    })
);
const MAX_NEXT_URL_LENGTH = 500;

function isSafeRedirectUrl(url: string): boolean {
  if (url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\')) {
    return true;
  }

  const resolved = toBrowserUrl(url);
  if (!resolved) return false;

  return ALLOWED_REDIRECT_ORIGINS.has(resolved.origin);
}

function toBrowserUrl(url: string): URL | null {
  if (typeof window === 'undefined') return null;

  try {
    // Use the full href (not just origin) as base so that relative URLs like
    // "login" are resolved the same way the browser would when setting
    // window.location.href — relative to the current page's directory path,
    // not just the origin root.
    return new URL(url, window.location.href);
  } catch {
    return null;
  }
}

// Authn-internal paths that are not valid post-login destinations.
const AUTHN_INTERNAL_PATHS = ['/login', '/signup', '/learner-dashboard'];

function isAuthnInternalPath(pathname: string): boolean {
  return AUTHN_INTERNAL_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

function getExplicitNextUrl(nextParam: string | null): string | undefined {
  if (!nextParam) return undefined;

  if (nextParam.length > MAX_NEXT_URL_LENGTH) return undefined;

  const resolved = toBrowserUrl(nextParam);
  if (!isSafeRedirectUrl(nextParam)) return undefined;

  const isAbsolute = /^https?:\/\//i.test(nextParam);
  const targetUrl = isAbsolute
    ? nextParam
    : resolved
      ? resolved.pathname + resolved.search + resolved.hash
      : nextParam;

  if (!targetUrl || targetUrl === '/') return undefined;

  if (resolved) {
    const currentPage = `${window.location.origin}${window.location.pathname}`;
    const resolvedPage = `${resolved.origin}${resolved.pathname}`;
    if (resolvedPage === currentPage) return undefined;
  }

  // Reject next URLs that point back into the authn app's own routes.
  if (
    resolved &&
    resolved.origin === window.location.origin &&
    isAuthnInternalPath(resolved.pathname)
  ) {
    return undefined;
  }

  return targetUrl;
}

function getRedirectUrl(user: AuthUser, explicitNextUrl?: string): string {
  if (explicitNextUrl) return explicitNextUrl;
  return isAdminUser(user) ? ADMIN_REDIRECT : LEARNER_REDIRECT;
}

export const LoginPage: FC = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<CredentialsValidationErrors>({});
  // When the password is correct but MFA is enrolled, the login response carries
  // a short-lived challenge token instead of a session. Holding it here swaps the
  // credentials form for the second-factor step.
  const [mfaChallenge, setMfaChallenge] = useState<{ token: string; expiresIn: number } | null>(
    null
  );

  // Get URL parameters
  const nextParam = searchParams.get('next');
  const tpaHint = searchParams.get('tpa_hint'); // Reserved for future SSO support

  // Determine redirect URL after login.
  // If ?next= is present, it always wins (explicit override).
  // Otherwise, route admins to the admin app and learners to the LMS dashboard.
  const explicitNextUrl = getExplicitNextUrl(nextParam);

  // Fetch CSRF token on mount
  const {
    data: csrfData,
    isLoading: isCsrfLoading,
    error: csrfError,
  } = useCsrfToken({
    forceRequest: true,
  });
  const { data: currentUser } = useCurrentUser();

  // Login mutation
  const loginMutation = useLogin();

  // Log tpa_hint for future SSO integration
  useEffect(() => {
    if (tpaHint) {
      console.info('TPA hint received:', tpaHint, '(SSO not yet implemented)');
    }
  }, [tpaHint]);

  useEffect(() => {
    if (currentUser) {
      window.location.replace(getRedirectUrl(currentUser, explicitNextUrl));
    }
  }, [currentUser, explicitNextUrl]);

  // Send the now-authenticated user to their destination. Shared by the
  // password path and the MFA-verify path; `user` may be null when the login
  // response body was dropped (dev proxy), in which case we fall back to
  // fetching /auth/me/.
  const redirectAfterAuth = useCallback(
    async (user: AuthUser | null) => {
      if (explicitNextUrl) {
        window.location.href = explicitNextUrl;
        return;
      }

      let resolved = user;
      if (!resolved) {
        try {
          const meResponse = await fetch('/api/centrix/v1/auth/me/', { credentials: 'include' });
          if (meResponse.ok) {
            resolved = await meResponse.json();
          }
        } catch {
          // /auth/me/ failed — continue with whatever we have
        }
      }

      window.location.href = resolved
        ? getRedirectUrl(resolved, explicitNextUrl)
        : LEARNER_REDIRECT;
    },
    [explicitNextUrl]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const errors = validateCredentials({ email, password });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    if (!csrfData?.csrfToken) {
      setError('Security token not available. Please refresh the page.');
      return;
    }

    try {
      const loginResult = await loginMutation.mutateAsync({
        email,
        password,
        csrfToken: csrfData.csrfToken,
      });

      // MFA-enrolled accounts get a challenge token instead of a session — swap
      // in the second-factor step and stop here until it is verified.
      if (isMfaRequiredResponse(loginResult)) {
        setMfaChallenge({
          token: loginResult.mfa_state_token,
          expiresIn: loginResult.expires_in_seconds,
        });
        return;
      }

      const user = (loginResult as LoginResponse | undefined)?.user ?? null;
      await redirectAfterAuth(user);
    } catch (err) {
      if (err instanceof ApiError) {
        // Handle specific API errors
        if (err.status === 401) {
          setError('Invalid email or password. Please try again.');
        } else if (err.status === 400) {
          // Check for field-specific errors
          const data = err.data as Record<string, string[]> | undefined;
          if (data?.email) {
            setError(data.email[0]);
          } else if (data?.password) {
            setError(data.password[0]);
          } else if (data?.non_field_errors) {
            setError(data.non_field_errors[0]);
          } else {
            setError(err.message);
          }
        } else {
          setError(err.message);
        }
      } else if (err instanceof Error) {
        // Handle network errors
        if (err.message.includes('fetch')) {
          setError('Unable to connect to the server. Please check your internet connection.');
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
      }
    }
  };

  const isLoading = loginMutation.isPending;
  const isFormDisabled = isLoading || isCsrfLoading;

  if (mfaChallenge) {
    return (
      <AuthLayout>
        <MfaChallengeForm
          stateToken={mfaChallenge.token}
          expiresInSeconds={mfaChallenge.expiresIn}
          onVerified={(user) => redirectAfterAuth(user)}
          onCancel={() => setMfaChallenge(null)}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Heading
        as="h2"
        className="text-[22px] leading-7 font-medium text-black mb-6 flex items-center gap-2"
      >
        Login
        {isCsrfLoading && <Spinner size="sm" className="text-brand-primary" />}
      </Heading>

      {/* CSRF Error */}
      <CollapsibleContainer isOpen={!!csrfError} className="mb-4">
        {csrfError && (
          <Alert variant="destructive">Failed to initialize login. Please refresh the page.</Alert>
        )}
      </CollapsibleContainer>

      {/* Login Error */}
      <CollapsibleContainer isOpen={!!error} className="mb-4">
        {error && (
          <Alert id="login-error" variant="destructive" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CollapsibleContainer>

      <form onSubmit={handleSubmit} className="space-y-2.5">
        <FormInputField
          id="email"
          type="email"
          label="Email"
          name="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setFieldErrors((prev) => ({ ...prev, email: undefined }));
          }}
          placeholder="Enter your email address"
          disabled={isFormDisabled}
          autoComplete="email"
          error={fieldErrors.email}
          inputClassName="h-11 px-4 text-sm text-black placeholder:text-gray-placeholder focus-visible:ring-offset-0 disabled:opacity-100 disabled:bg-gray-100 border-gray-placeholder"
        />

        <PasswordInputField
          id="password"
          label="Password"
          name="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setFieldErrors((prev) => ({ ...prev, password: undefined }));
          }}
          placeholder="Enter your password"
          disabled={isFormDisabled}
          autoComplete="current-password"
          error={fieldErrors.password}
          inputClassName="h-11 px-4 pr-12 text-sm text-black placeholder:text-gray-placeholder focus-visible:ring-offset-0 disabled:opacity-100 disabled:bg-gray-100 border-gray-placeholder"
        />
        {/* Forgot Password - between password and submit */}
        <div>
          <Link
            to="/forgot-password"
            className="text-sm leading-6 text-brand-primary hover:text-brand-primary/80 hover:underline"
          >
            Forgot Password?
          </Link>
        </div>

        <Button
          type="submit"
          variant="auth"
          size="auth"
          disabled={isFormDisabled}
          className="w-full leading-6"
        >
          {isLoading ? (
            <>
              <Spinner className="text-white" />
              <span>Logging in...</span>
            </>
          ) : (
            'Login'
          )}
        </Button>
      </form>

      {/* Sign up link */}
      <Paragraph className="mt-4.5 text-center text-sm leading-6 text-black">
        Don&apos;t have an account?{' '}
        <Link to="/signup" className="text-brand-primary font-medium hover:underline">
          Signup
        </Link>
      </Paragraph>
    </AuthLayout>
  );
};
